/**
 * WSH Help Hub controller — Phase 1 revision (2026-08-25)
 *
 * Scoped to `/pages/help` via presence of [data-wsh-help-filter]. No-op elsewhere.
 *
 * Behavior (per WSH_Help_Hub_Styling_Spec.md):
 *   - Intent chips (Show everything / Before you buy / After you buy)
 *   - Free-text search over question + answer + hidden_search_terms
 *   - Result count sentence, always visible (§4.5), with proper singular/plural
 *     ("1 question and 1 guide" vs "3 questions and 2 guides")
 *   - Guide-card visibility filters alongside questions
 *   - Empty state with contact link carrying the query as #wsh-help-contact?q=X
 *   - Per-question copy-link → clipboard + brief "Copied" flash
 *   - hashchange + on-load hash handling
 *   - ⌘K (Mac) / Ctrl+K (elsewhere) → focuses the help search input.
 *     Capture-phase handler, stopImmediatePropagation on match, so it
 *     doesn't collide with wsh-header.js:186 / predictive-search.js:94.
 *     Skips when focus is in a text field (per Phil's guidance — don't
 *     hijack typing).
 *   - "message us" jump link → smooth scroll to #wsh-help-contact,
 *     preventDefault + history.pushState (so my hashchange handler doesn't
 *     also fire, and the URL stays shareable). Does NOT reset the intent
 *     filter — non-filterable target, no reason to reset.
 *
 * Idempotent: guarded by window.__wshHelpHubInit.
 */

(function () {
  if (window.__wshHelpHubInit) return;
  window.__wshHelpHubInit = true;

  const SELECTORS = {
    root: '[data-wsh-help-filter]',
    intentBtn: '[data-wsh-intent-btn]',
    searchInput: '[data-wsh-help-search]',
    searchClear: '[data-wsh-help-search-clear]',
    searchKbd: '[data-wsh-help-search-kbd]',
    searchKbdMod: '[data-wsh-help-search-kbd-mod]',
    row: '[data-wsh-help-row]',
    category: '[data-wsh-help-category]',
    guidesGrid: '[data-wsh-help-guides]',
    guideCard: '[data-wsh-help-guide-card]',
    copyBtn: '[data-wsh-help-copy]',
    copyFlash: '[data-wsh-help-copy-flash]',
    count: '[data-wsh-help-count]',
    countText: '[data-wsh-help-count-text]',
    empty: '[data-wsh-help-empty]',
    emptyQuery: '[data-wsh-help-empty-query]',
    emptyContact: '[data-wsh-help-empty-contact]',
    jumpLink: '.wsh-help-intro__subhead a[href^="#"]',
  };

  const state = { intent: 'all', query: '' };
  let rowsCache = [];
  let categoriesCache = [];
  let guideCardsCache = [];
  let searchDebounce = null;
  let helpSearchInput = null;

  function init() {
    const filterRoot = document.querySelector(SELECTORS.root);
    if (!filterRoot) return;

    cacheDom();
    helpSearchInput = document.querySelector(SELECTORS.searchInput);

    swapKbdModForPlatform();
    bindIntent(filterRoot);
    bindSearch(filterRoot);
    bindCopyLinks();
    bindHash();
    bindKeyboardShortcut();
    bindJumpLinks();

    applyFilter(); // initial render — populates count sentence at rest
  }

  function cacheDom() {
    rowsCache = Array.from(document.querySelectorAll(SELECTORS.row));
    categoriesCache = Array.from(document.querySelectorAll(SELECTORS.category));
    guideCardsCache = Array.from(document.querySelectorAll(SELECTORS.guideCard));
  }

  // ---- ⌘K / Ctrl+K badge glyph swap ------------------------------------

  function isMacLike() {
    // Modern: userAgentData; fallback: platform + UA sniff.
    const uaData = navigator.userAgentData;
    if (uaData && typeof uaData.platform === 'string') {
      return /mac/i.test(uaData.platform);
    }
    const p = (navigator.platform || '') + ' ' + (navigator.userAgent || '');
    return /Mac|iPhone|iPod|iPad/.test(p);
  }

  function swapKbdModForPlatform() {
    if (isMacLike()) return; // ⌘ already correct
    document.querySelectorAll(SELECTORS.searchKbdMod).forEach((el) => {
      el.textContent = 'Ctrl+';
    });
  }

  // ---- Intent filter ----------------------------------------------------

  function bindIntent(root) {
    root.addEventListener('click', (e) => {
      const btn = e.target.closest(SELECTORS.intentBtn);
      if (!btn) return;
      const intent = btn.dataset.intent || 'all';
      setIntent(intent, /* scrollToList */ true);
    });
  }

  function setIntent(intent, scrollToList) {
    state.intent = intent;
    document.querySelectorAll(SELECTORS.intentBtn).forEach((b) => {
      const active = b.dataset.intent === intent;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-checked', String(active));
    });
    applyFilter();

    // Auto-scroll to first visible category when the user picks a chip and the
    // results row isn't already in the viewport. Fires on all viewports (was
    // mobile-only pre-2026-08-25 review) because Phil flagged that desktop
    // users also miss the update below the intro. Skips when the results are
    // already in view — the whole point is to make the change visible, not
    // to yank a user who's already looking at results.
    if (scrollToList) maybeScrollToResults();
  }

  function maybeScrollToResults() {
    const first = categoriesCache.find((c) => !c.hasAttribute('hidden'));
    if (!first) return;
    const rect = first.getBoundingClientRect();
    // "In view" = at least the top of the first category is within the top 80%
    // of the viewport. Threshold picked so a category peeking at the very
    // bottom (0-20% of viewport) still triggers a scroll.
    const inView = rect.top >= 0 && rect.top < window.innerHeight * 0.8;
    if (inView) return;
    first.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  // ---- Search -----------------------------------------------------------

  function bindSearch(root) {
    const input = root.querySelector(SELECTORS.searchInput);
    const clear = root.querySelector(SELECTORS.searchClear);
    if (!input) return;

    input.addEventListener('input', () => {
      clearTimeout(searchDebounce);
      searchDebounce = setTimeout(() => setQuery(input.value), 120);
    });

    if (clear) {
      clear.addEventListener('click', () => {
        input.value = '';
        setQuery('');
        input.focus();
      });
    }
  }

  function setQuery(query) {
    state.query = (query || '').trim();

    // Toggle clear button + kbd badge visibility based on populated state
    const clear = document.querySelector(SELECTORS.searchClear);
    const kbd = document.querySelector(SELECTORS.searchKbd);
    if (state.query !== '') {
      if (clear) clear.removeAttribute('hidden');
      if (kbd) kbd.setAttribute('hidden', '');
    } else {
      if (clear) clear.setAttribute('hidden', '');
      if (kbd) kbd.removeAttribute('hidden');
    }
    applyFilter();

    // NOTE: intentionally NO auto-scroll on search input — Phil 2026-08-25:
    // scrolling to the first result on any keystroke pushed the search field
    // out of view mid-typing, breaking the whole search flow. Chip clicks
    // still auto-scroll via setIntent → maybeScrollToResults because they
    // are discrete user actions; typing is not.
  }

  // ---- Apply filter -----------------------------------------------------

  function applyFilter() {
    const qLower = state.query.toLowerCase();
    const isFiltering = state.intent !== 'all' || state.query !== '';

    let visibleRowCount = 0;
    rowsCache.forEach((row) => {
      const rowIntent = row.dataset.intent || 'both';
      const blob = row.dataset.searchBlob || '';
      const intentMatch = state.intent === 'all' || rowIntent === state.intent || rowIntent === 'both';
      const queryMatch = qLower === '' || blob.indexOf(qLower) !== -1;
      const show = intentMatch && queryMatch;
      row.toggleAttribute('hidden', !show);
      if (show) visibleRowCount++;
    });

    categoriesCache.forEach((cat) => {
      const anyVisible = cat.querySelectorAll(SELECTORS.row + ':not([hidden])').length > 0;
      cat.toggleAttribute('hidden', !anyVisible);
    });

    let visibleGuideCount = 0;
    guideCardsCache.forEach((card) => {
      const cardIntent = card.dataset.intent || 'both';
      const intentMatch = state.intent === 'all' || cardIntent === state.intent || cardIntent === 'both';
      let queryMatch = true;
      if (qLower !== '') {
        const cardBlob = (card.textContent || '').toLowerCase();
        queryMatch = cardBlob.indexOf(qLower) !== -1;
      }
      const show = intentMatch && queryMatch;
      card.toggleAttribute('hidden', !show);
      if (show) visibleGuideCount++;
    });

    const guidesSection = document.querySelector(SELECTORS.guidesGrid);
    if (guidesSection) {
      guidesSection.toggleAttribute('hidden', visibleGuideCount === 0);
    }

    updateCount(visibleRowCount, rowsCache.length, visibleGuideCount, guideCardsCache.length, isFiltering);
    updateEmptyState(visibleRowCount + visibleGuideCount, isFiltering);
  }

  // ---- Count sentence (with singular/plural) ---------------------------

  function pluralize(n, singular, plural) {
    return `${n} ${n === 1 ? singular : plural}`;
  }

  function updateCount(shownRows, totalRows, shownGuides, totalGuides, isFiltering) {
    const el = document.querySelector(SELECTORS.count);
    if (!el) return;
    const textEl = el.querySelector(SELECTORS.countText);
    if (!textEl) return;

    // Hide the count when the empty state fires (avoids "0 questions and 0 guides"
    // stacking under the empty message).
    const shouldHide = isFiltering && shownRows + shownGuides === 0;
    el.toggleAttribute('hidden', shouldHide);
    if (shouldHide) return;

    const questionsFragment = pluralize(isFiltering ? shownRows : totalRows, 'question', 'questions');
    const guidesFragment = pluralize(isFiltering ? shownGuides : totalGuides, 'guide', 'guides');
    const lead = isFiltering ? 'Showing' : 'Showing all';
    textEl.textContent = `${lead} ${questionsFragment} and ${guidesFragment}`;
  }

  function updateEmptyState(visibleTotal, isFiltering) {
    const el = document.querySelector(SELECTORS.empty);
    if (!el) return;
    const show = isFiltering && visibleTotal === 0;
    el.toggleAttribute('hidden', !show);
    if (!show) return;

    const q = state.query;
    const qEl = el.querySelector(SELECTORS.emptyQuery);
    if (qEl) qEl.textContent = q || '(this filter)';

    const link = el.querySelector(SELECTORS.emptyContact);
    if (link) {
      const base = '#wsh-help-contact';
      if (q) {
        link.dataset.query = q;
        link.href = base + '?q=' + encodeURIComponent(q);
      } else {
        delete link.dataset.query;
        link.href = base;
      }
    }
  }

  // ---- Copy-link buttons ------------------------------------------------

  function bindCopyLinks() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest(SELECTORS.copyBtn);
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const anchor = btn.dataset.anchor;
      if (!anchor) return;
      const url = window.location.origin + window.location.pathname + '#' + anchor;
      copyToClipboard(url).then(() => flashCopied(btn));
    });
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    }
    return Promise.resolve(fallbackCopy(text));
  }

  function fallbackCopy(text) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (_) { /* ignore */ }
    document.body.removeChild(ta);
  }

  function flashCopied(btn) {
    const flash = btn.querySelector(SELECTORS.copyFlash);
    if (!flash) return;
    btn.classList.add('is-copied');
    flash.removeAttribute('aria-hidden');
    clearTimeout(btn.__copiedTimer);
    btn.__copiedTimer = setTimeout(() => {
      btn.classList.remove('is-copied');
      flash.setAttribute('aria-hidden', 'true');
    }, 1400);
  }

  // ---- Hash handling ----------------------------------------------------
  //
  // Per spec §4.1 + Phil's clarification: the intent-to-all reset ONLY
  // applies to targets inside the filterable category tree (redirects
  // landing on #downloads or #midi-keyboard). Non-filterable anchors like
  // #wsh-help-contact must preserve the user's current filter state.

  function bindHash() {
    handleHash();
    window.addEventListener('hashchange', handleHash);
  }

  function handleHash() {
    const raw = (window.location.hash || '').replace(/^#/, '').split('?')[0];
    if (!raw) return;
    const target = document.getElementById(raw);
    if (!target) return;

    const isFilterable = target.closest('[data-wsh-help-category]') !== null;
    if (isFilterable) {
      setIntent('all', /* scrollToList */ false);
    }

    requestAnimationFrame(() => {
      if (target.tagName === 'DETAILS') target.open = true;
      target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    });
  }

  // ---- "message us" jump link (§4.1) -----------------------------------
  //
  // Uses preventDefault + history.pushState so hashchange does NOT fire,
  // so handleHash's intent-reset logic doesn't run. Filter state preserved.
  // Smooth scroll respects prefers-reduced-motion.

  function bindJumpLinks() {
    document.querySelectorAll(SELECTORS.jumpLink).forEach((link) => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href') || '';
        if (!href.startsWith('#')) return; // let external links behave normally
        const targetId = href.slice(1).split('?')[0];
        const target = document.getElementById(targetId);
        if (!target) return; // let default behavior fire
        e.preventDefault();
        target.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'start',
        });
        if (window.location.hash !== href) {
          try { history.pushState(null, '', href); } catch (_) { /* older browsers */ }
        }
      });
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // ---- Keyboard shortcut: Cmd+K / Ctrl+K --------------------------------
  //
  // Capture-phase + stopImmediatePropagation guarantees we fire BEFORE
  // wsh-header.js:187 and predictive-search.js:94, which both listen at
  // bubble phase for the same shortcut and would otherwise open the
  // header search overlay when the customer intends to search this hub.
  //
  // Skip when focus is in a text-like input so ⌘K doesn't hijack typing.
  // If focus is already inside the help search input, still block the
  // header handler (would yank focus away) but don't re-focus/re-select.

  function bindKeyboardShortcut() {
    document.addEventListener('keydown', (e) => {
      if (!isSearchShortcut(e)) return;
      const helpInput = helpSearchInput || document.querySelector(SELECTORS.searchInput);
      if (!helpInput) return; // not on help hub — let site-wide handler run

      const active = document.activeElement;
      const inHelpInput = active === helpInput;
      const inOtherTextField = !inHelpInput && isTextInputElement(active);

      if (inOtherTextField) return; // don't hijack typing

      // Own the shortcut: block header search overlay + predictive-search from firing.
      e.preventDefault();
      e.stopImmediatePropagation();

      if (!inHelpInput) {
        helpInput.focus({ preventScroll: false });
        // Do NOT select-all — respects existing text if the input has content.
      }
    }, { capture: true });
  }

  function isSearchShortcut(e) {
    return (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && (e.key === 'k' || e.key === 'K');
  }

  function isTextInputElement(el) {
    if (!el || !(el instanceof HTMLElement)) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName;
    if (tag === 'TEXTAREA') return true;
    if (tag !== 'INPUT') return false;
    const type = (el.type || 'text').toLowerCase();
    return ['text', 'search', 'email', 'tel', 'url', 'password', 'number'].includes(type);
  }

  // ---- Boot -------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * WSH Help Hub — Phase 2 §3 mailto-composer form controller
 * (2026-08-26 — replaces the old wsh-contact-doorway button pair)
 * (2026-09-05 — Klaviyo emit re-routed through WSH CMS proxy per Path A)
 *
 * Scoped to /pages/help contact panel via [data-wsh-help-contact-form].
 * No-op elsewhere. Idempotent via window.__wshHelpFormInit.
 *
 * Behavior:
 *   - Query carry-through: reads ?q=<query> from location.hash (matches
 *     the empty-state link pattern from Phase 1 wsh-help-hub.js:275),
 *     pre-fills the message textarea.
 *   - Intent-chip preset: reads Phase 1's active intent chip (.is-active)
 *     and presets category to "Question before I buy" (pre) or
 *     "Can't download or unzip" (post). Only presets if user hasn't
 *     already selected a category (default, not lock).
 *   - Conditional field reveal per category (per brief §3.2).
 *   - Live mailto composition: on every field input/change, rebuilds
 *     the preview panel AND updates the anchor's mailto: href, so the
 *     click always fires the current composition.
 *   - Character counter: reveals only past 2000 chars (per Phil — avoids
 *     making people self-edit before they've explained the problem).
 *   - Copy button: copies the composed message to clipboard.
 *   - Klaviyo emit on click: fire-and-forget POST to the WSH CMS proxy
 *     endpoint /wsh/klaviyo-support-request, which fires the "Support
 *     Request Submitted" event server-side via Klaviyo Private API.
 *     Never blocks the mailto navigation. Never sends message body.
 *     Never sends order number itself (boolean only). Identifier
 *     priority: order-email → __kla_id cookie → skip silently (Option
 *     A per Phil 2026-08-26). Pre-2026-09-05 the fetch went directly to
 *     https://a.klaviyo.com/client/events/ but that endpoint has been
 *     CORS-blocked on this store since 2026-08-26 deploy — every
 *     structured emit was lost silently. See Layer 3 durable D1.
 */
(function () {
  if (window.__wshHelpFormInit) return;

  const form = document.querySelector('[data-wsh-help-contact-form]');
  if (!form) return;
  window.__wshHelpFormInit = true;

  const SUPPORT_EMAIL = 'support@wesoundhuman.com';
  const CMS_SUPPORT_ENDPOINT = 'https://cms.wesoundhuman.com/wsh/klaviyo-support-request';
  const COUNTER_THRESHOLD = 2000;
  const MAX_MESSAGE_LEN = 2500;

  // Category → which conditional fields to reveal (per brief §3.2 table).
  // Order number + order email are always-visible optional — not in this map.
  const CATEGORY_FIELDS = {
    "Can't download or unzip":         ['os', 'browser'],
    "Kontakt or DAW loading problem":  ['os', 'browser', 'daw'],
    "Question before I buy":           ['daw'],
    "Order, refund or discount":       [],
    "Licensing question":              [],
    "Something else":                  [],
  };

  // Phase 1 intent chip → Phase 2 category preset (per brief §3.4).
  const INTENT_TO_CATEGORY = {
    pre:  'Question before I buy',
    post: "Can't download or unzip",
  };

  const els = {
    conditional: form.querySelector('[data-wsh-help-form-conditional]'),
    fields: {
      category:    form.querySelector('[data-wsh-help-form-category]'),
      message:     form.querySelector('[data-wsh-help-form-message]'),
      os:          form.querySelector('[data-wsh-help-form-os]'),
      browser:     form.querySelector('[data-wsh-help-form-browser]'),
      daw:         form.querySelector('[data-wsh-help-form-daw]'),
      orderNumber: form.querySelector('[data-wsh-help-form-order-number]'),
      orderEmail:  form.querySelector('[data-wsh-help-form-order-email]'),
    },
    fieldWraps: {
      os:      form.querySelector('[data-wsh-help-form-field="os"]'),
      browser: form.querySelector('[data-wsh-help-form-field="browser"]'),
      daw:     form.querySelector('[data-wsh-help-form-field="daw"]'),
    },
    preview:   form.querySelector('[data-wsh-help-form-preview]'),
    submit:    form.querySelector('[data-wsh-help-form-submit]'),
    copyBtn:   form.querySelector('[data-wsh-help-form-copy]'),
    copyLabel: form.querySelector('[data-wsh-help-form-copy-label]'),
    count:     form.querySelector('[data-wsh-help-form-count]'),
    countNum:  form.querySelector('[data-wsh-help-form-count-num]'),
  };

  let searchQueryFromUrl = ''; // captured for the Klaviyo emit properties

  applyQueryCarryThrough();
  applyIntentPreset();
  bindFieldListeners();
  bindCopyButton();
  bindSubmitClick();
  updatePreviewAndHref();

  function applyQueryCarryThrough() {
    // Phase 1's empty-state link builds #wsh-help-contact?q=<encoded>
    // (query params INSIDE the fragment, not before). Parse accordingly.
    const hash = window.location.hash || '';
    const qIdx = hash.indexOf('?');
    if (qIdx === -1) return;
    const params = new URLSearchParams(hash.slice(qIdx + 1));
    const q = params.get('q');
    if (!q) return;
    searchQueryFromUrl = q;
    if (els.fields.message && !els.fields.message.value) {
      els.fields.message.value = q;
      updateCharCount();
    }
  }

  function applyIntentPreset() {
    // Read active intent chip from Phase 1 filter (DOM query — the
    // filter's own controller keeps aria-checked + .is-active in sync).
    const activeBtn = document.querySelector('[data-wsh-intent-btn].is-active');
    if (!activeBtn) return;
    const preset = INTENT_TO_CATEGORY[activeBtn.dataset.intent];
    if (!preset) return;
    // Only preset if user hasn't already picked one (?q= arrived after
    // they interacted with the form → don't override).
    if (els.fields.category && !els.fields.category.value) {
      els.fields.category.value = preset;
      applyCategoryConditionalReveal(preset);
    }
  }

  function bindFieldListeners() {
    els.fields.category.addEventListener('change', () => {
      applyCategoryConditionalReveal(els.fields.category.value);
      updatePreviewAndHref();
    });
    ['message', 'os', 'browser', 'daw', 'orderNumber', 'orderEmail'].forEach((k) => {
      const el = els.fields[k];
      if (!el) return;
      el.addEventListener('input', () => {
        if (k === 'message') updateCharCount();
        updatePreviewAndHref();
      });
      el.addEventListener('change', updatePreviewAndHref);
    });
  }

  function applyCategoryConditionalReveal(category) {
    const revealFields = CATEGORY_FIELDS[category] || [];
    const anyRevealed = revealFields.length > 0;
    els.conditional.toggleAttribute('hidden', !anyRevealed);
    ['os', 'browser', 'daw'].forEach((f) => {
      const wrap = els.fieldWraps[f];
      if (!wrap) return;
      const show = revealFields.includes(f);
      wrap.toggleAttribute('hidden', !show);
      // Clear value on hide so it doesn't leak into a subsequent category
      if (!show && els.fields[f]) els.fields[f].value = '';
    });
  }

  function updateCharCount() {
    const len = (els.fields.message.value || '').length;
    if (len >= COUNTER_THRESHOLD) {
      els.count.hidden = false;
      els.countNum.textContent = String(Math.max(0, MAX_MESSAGE_LEN - len));
    } else {
      els.count.hidden = true;
    }
  }

  function readValues() {
    return {
      category:    (els.fields.category.value    || '').trim(),
      message:     (els.fields.message.value     || '').trim(),
      os:          (els.fields.os?.value          || '').trim(),
      browser:     (els.fields.browser?.value     || '').trim(),
      daw:         (els.fields.daw?.value         || '').trim(),
      orderNumber: (els.fields.orderNumber?.value || '').trim(),
      orderEmail:  (els.fields.orderEmail?.value  || '').trim(),
    };
  }

  function buildSubject(v) {
    return v.category ? `[${v.category}]` : '[Support request]';
  }

  function buildBody(v) {
    const lines = [`Category: ${v.category || '(not selected)'}`];
    if (v.os)          lines.push(`OS: ${v.os}`);
    if (v.browser)     lines.push(`Browser: ${v.browser}`);
    if (v.daw)         lines.push(`DAW: ${v.daw}`);
    if (v.orderNumber) lines.push(`Order number: ${v.orderNumber}`);
    if (v.orderEmail)  lines.push(`Order email: ${v.orderEmail}`);
    lines.push('');
    lines.push('Message:');
    lines.push(v.message || '(empty)');
    return lines.join('\r\n');
  }

  function buildMailto(v) {
    const subject = encodeURIComponent(buildSubject(v));
    const body    = encodeURIComponent(buildBody(v));
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  }

  function updatePreviewAndHref() {
    const v = readValues();
    // Preview uses \n (LF) for display in <pre>; body encoding uses \r\n (CRLF)
    // for mailto RFC-safety per Phil's spec.
    const bodyForPreview = buildBody(v).replace(/\r\n/g, '\n');
    els.preview.textContent = `To:      ${SUPPORT_EMAIL}\nSubject: ${buildSubject(v)}\n\n${bodyForPreview}`;
    els.submit.href = buildMailto(v);
  }

  function bindCopyButton() {
    els.copyBtn.addEventListener('click', () => {
      const text = els.preview.textContent;
      copyToClipboard(text).then(() => flashCopied());
    });
  }

  function flashCopied() {
    const orig = 'Copy message';
    els.copyLabel.textContent = 'Copied';
    clearTimeout(flashCopied._t);
    flashCopied._t = setTimeout(() => { els.copyLabel.textContent = orig; }, 1400);
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    }
    return Promise.resolve(fallbackCopy(text));
  }

  function fallbackCopy(text) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (_) { /* ignore */ }
    document.body.removeChild(ta);
  }

  // ---- Submit click: fire Klaviyo emit before mailto navigation ----

  function bindSubmitClick() {
    els.submit.addEventListener('click', () => {
      // No preventDefault — let anchor's default action open mailto in new tab.
      // Emit is fire-and-forget; wrapped so any throw is swallowed.
      try { emitKlaviyo(readValues()); }
      catch (_) { /* analytics never blocks support */ }
    });
  }

  function getKlaviyoAnonymousId() {
    // __kla_id cookie is URL-encoded JSON like {"anonymous":"anon-..."}
    // or with a $-prefixed key in newer versions. Try both.
    const match = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
    if (!match) return null;
    try {
      const decoded = JSON.parse(decodeURIComponent(match[1]));
      return decoded.$anonymous || decoded.anonymous || null;
    } catch (_) {
      return null;
    }
  }

  function emitKlaviyo(v) {
    // Identifier priority: order-email → __kla_id anonymous_id → skip.
    const orderEmailValid = v.orderEmail && /@/.test(v.orderEmail);
    const anonId = orderEmailValid ? null : getKlaviyoAnonymousId();
    if (!orderEmailValid && !anonId) return; // Option A silent skip

    const properties = { category: v.category || null };
    if (v.os)      properties.os      = v.os;
    if (v.browser) properties.browser = v.browser;
    if (v.daw)     properties.daw     = v.daw;
    properties.order_number_supplied = Boolean(v.orderNumber);
    properties.order_email_supplied  = Boolean(orderEmailValid);
    if (searchQueryFromUrl) properties.search_query = searchQueryFromUrl;

    // Server assembles the Klaviyo JSON-API payload (metric name, revision,
    // profile block by email or anonymous_id) — this endpoint takes the flat
    // shape and does the shape conversion server-side, so the theme doesn't
    // carry Klaviyo API version knowledge.
    const payload = {
      order_email:  orderEmailValid ? v.orderEmail : null,
      anonymous_id: anonId || null,
      properties:   properties,
    };

    try {
      // Fire-and-forget to CMS proxy. keepalive: true so the request survives
      // the mailto:-in-new-tab navigation. Any failure (CORS, network, 4xx/
      // 5xx) is silently swallowed — the mailto is the delivery mechanism,
      // this event is analytics-only. Pre-2026-09-05 the fetch went direct
      // to a.klaviyo.com/client/events/ which CORS-blocks on this store —
      // every emit was lost silently for ~9 days before Path A landed.
      fetch(CMS_SUPPORT_ENDPOINT, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => { /* swallow */ });
    } catch (_) { /* swallow */ }
  }
})();
